"use client";

import dynamic from "next/dynamic";

import type { InfiniteCounterProps } from "./InfiniteCounterImpl";

export type { InfiniteCounterProps };

// Keep in sync with InfiniteCounterImpl.tsx — duplicated so the wrapper module
// stays free of class-variance-authority / motion / next-intl imports.
const DIGIT_GRADIENT = "linear-gradient(180deg, #EBE5E7 7.01%, rgba(235, 229, 231, 0.20) 100%)";

// 9 chars in "5,000,000" → maxCqw = 150 / 9 ≈ 16.67 (mirrors Impl's
// `Math.min(150 / charCount, 22)` so SSR + hydrated paint at the same size).
const FALLBACK_MAX_CQW = 150 / 9;

function CounterShellFallback() {
  return (
    <div className='tw-relative tw-flex tw-flex-col tw-items-center' style={{ containerType: "inline-size" }}>
      <div className='tw-relative tw-flex tw-w-full tw-items-center tw-justify-center'>
        <span
          aria-hidden='true'
          className='tw-select-none tw-whitespace-nowrap tw-font-medium tw-leading-none tw-tracking-tight tw-type-6xl'
          style={{
            fontVariantNumeric: "tabular-nums",
            fontSize: `clamp(2rem, ${FALLBACK_MAX_CQW}cqw, 14rem)`,
          }}
        >
          {/* inline-block + overflow-hidden + height:1em mirrors
              InfiniteCounterImpl's per-digit wrappers. The overflow-hidden is
              load-bearing: it shifts the inline-block's baseline to its bottom
              edge, expanding the line box ~14% so SSR + hydrated heights match
              and the caption (-mt-6) lands at the same vertical position. */}
          <span
            className='tw-inline-block tw-overflow-hidden tw-bg-clip-text tw-text-transparent'
            style={{ height: "1em", backgroundImage: DIGIT_GRADIENT, WebkitBackgroundClip: "text" }}
          >
            5,000,000
          </span>
        </span>
      </div>
    </div>
  );
}

// 20-element digit strip rendered server-side (~25 KB) is animation overhead the
// SEO crawl never benefits from. Defer the slot machine to the client; the
// fallback uses the same containerType + cqw font sizing as the Impl so SSR,
// hydration, and the animating counter all paint at identical dimensions.
const DeferredCounter = dynamic(() => import("./InfiniteCounterImpl").then(m => m.InfiniteCounterImpl), {
  ssr: false,
  loading: () => <CounterShellFallback />,
});

export function InfiniteCounter({ caption, ...rest }: InfiniteCounterProps) {
  return (
    <>
      <DeferredCounter {...rest} />
      {caption && (
        <h3 className='-tw-mt-6 tw-relative tw-mx-auto tw-max-w-xl tw-text-pretty tw-text-center md:-tw-mt-14 tw-type-5xl tw-text-black'>
          {caption}
        </h3>
      )}
    </>
  );
}
